import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiArrowLeft, FiCheck, FiEdit2, FiLogOut, FiMoreHorizontal, FiShield, FiShieldOff, FiUserMinus, FiUserPlus, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { addGroupMembers, leaveGroup, makeGroupAdmin, removeGroupAdmin, removeGroupMember, renameGroup } from "../api/messages";
import useLockBackgroundScroll from "../hooks/useLockBackgroundScroll";
import { groupRole, memberActions, personName, runsGroup } from "../utils/groupChats";
import GroupAvatar from "./GroupAvatar";
import GroupMembersPicker from "./GroupMembersPicker";
import UserProfileLink from "./UserProfileLink";

const idOf = (value) => String(value?._id ?? value ?? "");
const MAX_NAME = 60;

/**
 * A group's details: its name and who's in it, with who moderates it and who's admin.
 * Anyone leaves. The moderator and admins rename it, add people and remove them; the
 * moderator also makes people admins and ordinary members again. `onChanged(conversation)`
 * after each change; `onLeft()` once this person has left.
 */
const GroupInfoModal = ({ open, conversation, currentUser, onClose, onChanged, onLeft, isDark }) => {
  const { t } = useTranslation();
  const myId = idOf(currentUser?.id ?? currentUser?._id);
  const [view, setView] = useState("info"); // "info" | "add"
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(conversation?.name || "");
  const [adding, setAdding] = useState([]);
  const [busy, setBusy] = useState(null);
  /** Why the last change didn't go through. Shown in here: the app's toasts sit under this dialog. */
  const [error, setError] = useState("");
  /** The member whose options (make admin, remove…) are showing. */
  const [optionsFor, setOptionsFor] = useState(null);
  const nameInput = useRef(null);

  useLockBackgroundScroll(open);

  useEffect(() => {
    if (!open) {
      setView("info");
      setEditing(false);
      setAdding([]);
      setOptionsFor(null);
      setError("");
      return undefined;
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!editing) setName(conversation?.name || "");
  }, [conversation?.name, editing]);

  // Renaming starts with the whole name selected, ready to type over.
  useEffect(() => {
    if (editing) nameInput.current?.select();
  }, [editing]);

  useEffect(() => setError(""), [view]);

  if (!open || !conversation) return null;

  const failed = (err) => setError(err?.response?.data?.message || t("groups.updateError"));

  /** Run a change; the group as it now is goes to the chat. */
  const act = async (key, request, done) => {
    setBusy(key);
    setError("");
    try {
      const res = await request();
      if (res.success && res.data) onChanged?.(res.data);
      done?.(res);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const saveName = (e) => {
    e.preventDefault();
    const next = name.trim();
    if (!next || next === conversation.name) return setEditing(false);
    act("rename", () => renameGroup(conversation._id, next), () => setEditing(false));
  };

  const remove = (member) => {
    if (!window.confirm(t("groups.confirmRemove", { name: personName(member, t) }))) return;
    act(`remove:${idOf(member)}`, () => removeGroupMember(conversation._id, idOf(member)), () => setOptionsFor(null));
  };

  const memberAction = (action, member) => {
    if (action === "remove") return remove(member);
    const request = action === "admin" ? makeGroupAdmin : removeGroupAdmin;
    act(`${action}:${idOf(member)}`, () => request(conversation._id, idOf(member)), () => setOptionsFor(null));
  };

  const leave = async () => {
    if (!window.confirm(t("groups.confirmLeave", { name: conversation.name }))) return;
    setBusy("leave");
    setError("");
    try {
      await leaveGroup(conversation._id, myId);
      onLeft?.();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const canEdit = runsGroup(conversation, myId);
  const members = [...(conversation.participants || [])].sort((a, b) => {
    // You first, then the moderator, the admins, and everyone else by name.
    const rank = (m) => (idOf(m) === myId ? 0 : { moderator: 1, admin: 2 }[groupRole(conversation, m)] ?? 3);
    return rank(a) - rank(b) || String(a.username || "").localeCompare(String(b.username || ""));
  });
  const actionLabel = { admin: "groups.makeAdmin", unadmin: "groups.removeAdmin", remove: "groups.remove" };
  const actionIcon = { admin: FiShield, unadmin: FiShieldOff, remove: FiUserMinus };

  const muted = "text-label-secondary";
  const base = "text-label-primary";
  const divider = "border-separator-default";
  const iconBtn = `grid h-8 w-8 place-items-center rounded-radius-xs transition disabled:opacity-40 hover:bg-fill-quaternary`;

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-end justify-center sm:items-center sm:p-4 bg-overlay-scrim"
      onClick={onClose}
      data-scroll-lock-modal
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={conversation.name}
        onClick={(e) => e.stopPropagation()}
        className={`flex h-[85vh] max-h-[680px] w-full flex-col overflow-hidden rounded-t-radius-xl border shadow-overlay sm:max-w-md sm:rounded-radius-lg ${
          "border-separator-default bg-bg-raised"
        }`}
      >
        <div className={`flex items-center gap-2 border-b px-3 py-2.5 ${divider}`}>
          {view === "add" && (
            <button type="button" onClick={() => setView("info")} className={iconBtn} aria-label={t("common.back")}>
              <FiArrowLeft className={`h-5 w-5 ${muted}`} />
            </button>
          )}
          <h2 className={`flex-1 px-1 text-headline ${base}`}>{view === "add" ? t("groups.addPeople") : t("groups.groupInfo")}</h2>
          <button type="button" onClick={onClose} aria-label={t("common.close")} className={iconBtn}>
            <FiX className={`h-5 w-5 ${muted}`} />
          </button>
        </div>

        {error && (
          <p role="alert" className={`border-b px-4 py-2.5 text-footnote font-medium border-status-danger bg-status-danger-bg text-status-danger`}>
            {error}
          </p>
        )}

        {view === "add" ? (
          <>
            <div className="pt-3" />
            <GroupMembersPicker
              selected={adding}
              onChange={setAdding}
              exclude={(conversation.participants || []).map(idOf)}
              isDark={isDark}
              autoFocus
            />
            <div className={`flex items-center justify-between gap-3 border-t px-4 py-3 ${divider}`}>
              <span className={`text-footnote ${muted}`}>
                {adding.length ? t("groups.peopleSelected", { count: adding.length }) : t("groups.pickSomeone")}
              </span>
              <button
                type="button"
                disabled={!adding.length || busy === "add"}
                onClick={() =>
                  act("add", () => addGroupMembers(conversation._id, adding.map(idOf)), () => {
                    setAdding([]);
                    setView("info");
                  })
                }
                className="rounded-radius-sm bg-accent-default px-5 py-2.5 text-subheadline-semibold text-accent-on transition hover:bg-mintStrong disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("groups.add")}
              </button>
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-scroll-lock-target>
            <div className="flex flex-col items-center gap-2 px-4 pb-4 pt-5 text-center">
              <GroupAvatar size="xl" isDark={isDark} />
              {editing ? (
                <form onSubmit={saveName} className="flex w-full max-w-xs items-center gap-2">
                  <input
                    value={name}
                    maxLength={MAX_NAME}
                    onChange={(e) => setName(e.target.value)}
                    ref={nameInput}
                    aria-label={t("groups.name")}
                    className={`min-w-0 flex-1 rounded-radius-sm border px-3 py-2 text-subheadline outline-none focus:border-accent-default ${
                      "border-separator-default bg-bg-raised text-label-primary"
                    }`}
                  />
                  <button type="submit" disabled={!name.trim() || busy === "rename"} className="grid h-9 w-9 place-items-center rounded-radius-sm bg-accent-default text-accent-on disabled:opacity-40" aria-label={t("common.save")}>
                    <FiCheck className="h-4 w-4" />
                  </button>
                </form>
              ) : (
                <div className="flex items-center gap-1">
                  <h3 className={`text-title-3 ${base}`}>{conversation.name}</h3>
                  {canEdit && (
                    <button type="button" onClick={() => setEditing(true)} className={iconBtn} aria-label={t("groups.rename")} title={t("groups.rename")}>
                      <FiEdit2 className={`h-4 w-4 ${muted}`} />
                    </button>
                  )}
                </div>
              )}
              <p className={`text-footnote ${muted}`}>{t("groups.memberCount", { count: members.length })}</p>
            </div>

            <div className={`border-t ${divider}`}>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setView("add")}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left text-subheadline-semibold text-accent-text transition hover:bg-fill-quaternary`}
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-accent-tinted">
                    <FiUserPlus className="h-4 w-4" />
                  </span>
                  {t("groups.addPeople")}
                </button>
              )}
              {members.map((member) => {
                const id = idOf(member);
                const me = id === myId;
                const role = groupRole(conversation, member);
                const actions = memberActions(conversation, myId, member);
                const showing = optionsFor === id && actions.length > 0;
                return (
                  <div key={id} className={showing ? ("bg-fill-tertiary") : ""}>
                    <div className="flex items-center gap-3 px-4 py-2">
                      <UserProfileLink
                        user={member}
                        avatarSize="lg"
                        isDark={isDark}
                        nameClassName={`text-subheadline-semibold ${base}`}
                        className="min-w-0 flex-1"
                        fallback={t("common.user")}
                      >
                        {me ? t("groups.youCapital") : undefined}
                      </UserProfileLink>
                      {role && (
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-caption-1-semibold ${
                            role === "moderator"
                              ? isDark ? "bg-accent-default text-accent-on" : "bg-emerald-600 text-white"
                              : isDark ? "bg-accent-tinted text-accent-text" : "bg-accent-tinted text-status-success"
                          }`}
                        >
                          {t(`groups.${role}`)}
                        </span>
                      )}
                      {actions.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setOptionsFor(showing ? null : id)}
                          className={iconBtn}
                          aria-expanded={showing}
                          aria-label={t("groups.memberOptions", { name: personName(member, t) })}
                          title={t("groups.memberOptions", { name: personName(member, t) })}
                        >
                          <FiMoreHorizontal className={`h-4 w-4 ${muted}`} />
                        </button>
                      )}
                    </div>
                    {showing && (
                      <div className="flex flex-wrap gap-2 px-4 pb-3 pl-[4.25rem]">
                        {actions.map((action) => {
                          const Icon = actionIcon[action];
                          const danger = action === "remove";
                          return (
                            <button
                              key={action}
                              type="button"
                              onClick={() => memberAction(action, member)}
                              disabled={!!busy}
                              className={`inline-flex items-center gap-1.5 rounded-radius-xs border px-3 py-1.5 text-footnote-semibold transition disabled:opacity-50 ${
                                danger
                                  ? "border-status-danger text-status-danger hover:bg-status-danger-bg"
                                  : isDark ? "border-white/10 text-slate-200 hover:bg-white/10" : "border-slate-200 text-slate-700 hover:bg-white"
                              }`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                              {t(actionLabel[action])}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={`mt-2 border-t px-4 py-3 ${divider}`}>
              <button
                type="button"
                onClick={leave}
                disabled={busy === "leave"}
                className={`flex w-full items-center gap-3 rounded-radius-sm px-2 py-2.5 text-left text-subheadline-semibold text-status-danger transition disabled:opacity-50 hover:bg-status-danger-bg`}
              >
                <FiLogOut className="h-4 w-4" />
                {t("groups.leave")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GroupInfoModal;
